import { IconButton, Tooltip } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { GREY } from "@/config/mui/colorPalette";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useErrorLogger from "@/utils/useErrorLogger";
import { QuestionCircleIcon } from "../icons";

const MAX_RETRIES = 10;
const RETRY_INTERVAL_MS = 500;

export default function IntercomCustomLauncher() {
  const logError = useErrorLogger();
  const { intercomV1Enabled } = useFeatureFlags();
  const [isIntercomInitialized, setIsIntercomInitialized] = useState(false);
  const retryCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkIntercom = useCallback(() => {
    if (typeof window !== "undefined" && window.intercomSettings) {
      setIsIntercomInitialized(true);

      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    if (!intercomV1Enabled) return;

    retryCountRef.current = 0;

    if (checkIntercom()) return;

    intervalRef.current = setInterval(() => {
      if (checkIntercom()) {
        clearInterval(intervalRef.current!);

        return;
      }

      retryCountRef.current += 1;

      if (retryCountRef.current >= MAX_RETRIES) {
        clearInterval(intervalRef.current!);
        logError(
          "IntercomCustomLauncher: Failed to detect intercomSettings after max retries."
        );
      }
    }, RETRY_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intercomV1Enabled, logError, checkIntercom]);

  if (!intercomV1Enabled || !isIntercomInitialized) return null;

  return (
    <Tooltip title="Support" arrow placement="bottom">
      <IconButton id="intercom-launcher">
        <QuestionCircleIcon color={GREY[70]} size={22} />
      </IconButton>
    </Tooltip>
  );
}
